/**
 * Timer state model shared by server and clients.
 *
 * The single source of truth for "how much time is left" is a time anchor
 * (`endAt`) while running, or a frozen `remainingMs` while idle/paused.
 * Nothing ever decrements a counter; remaining time is always derived from
 * the current clock reading.
 */

export type PresetId = 'talk' | 'qa';

export interface Preset {
  id: PresetId;
  label: string;
  durationMs: number;
}

export const PRESETS: Record<PresetId, Preset> = {
  talk: { id: 'talk', label: 'Vortrag', durationMs: 12 * 60 * 1000 },
  qa: { id: 'qa', label: 'Q&A', durationMs: 7 * 60 * 1000 },
};

export const PRESET_IDS: readonly PresetId[] = ['talk', 'qa'];

export function isPresetId(value: unknown): value is PresetId {
  return value === 'talk' || value === 'qa';
}

/** Persisted status. `finished` is derived (running with 0 ms left), never stored. */
export type CoreStatus = 'idle' | 'running' | 'paused';
export type Status = CoreStatus | 'finished';

export interface TimerState {
  presetId: PresetId;
  /** Initial duration of the selected preset. */
  durationMs: number;
  status: CoreStatus;
  /**
   * Authoritative remaining time while idle/paused.
   * While running it is only a snapshot taken at `updatedAt` (informational).
   */
  remainingMs: number;
  /**
   * Time anchor in the LOCAL device's `Date.now()` epoch, only while running.
   * Never transmitted over the wire (device clocks differ); see protocol.ts.
   */
  endAt: number | null;
  /** Monotonically increasing per effective user action. */
  version: number;
  /** Wall-clock time of the device that issued the last action (tie-break only). */
  updatedAt: number;
  /** Client id of the device that issued the last action (final tie-break). */
  originId: string;
}

export const DEFAULT_PRESET: PresetId = 'talk';

export function createInitialState(): TimerState {
  const preset = PRESETS[DEFAULT_PRESET];
  return {
    presetId: preset.id,
    durationMs: preset.durationMs,
    status: 'idle',
    remainingMs: preset.durationMs,
    endAt: null,
    version: 0,
    updatedAt: 0,
    originId: 'init',
  };
}

/** Remaining milliseconds at a given clock reading; never negative. */
export function remainingAt(state: TimerState, now: number): number {
  if (state.status === 'running') {
    if (state.endAt === null) return 0;
    return Math.max(0, state.endAt - now);
  }
  return Math.max(0, state.remainingMs);
}

/** Effective status including the derived `finished` state. */
export function statusAt(state: TimerState, now: number): Status {
  if (state.status === 'running' && remainingAt(state, now) <= 0) return 'finished';
  return state.status;
}

/**
 * Deterministic ordering of two states. Returns >0 if `a` is newer than `b`.
 *
 * 1. higher `version` wins
 * 2. on equal version, later `updatedAt` wins (conflict after offline divergence:
 *    "the last deliberate user action wins")
 * 3. on equal `updatedAt`, lexically larger `originId` wins (pure tie-break so
 *    every node converges on the same state)
 */
export function compareStates(
  a: TimerState | StateIdentity,
  b: TimerState | StateIdentity,
): number {
  if (a.version !== b.version) return a.version - b.version;
  if (a.updatedAt !== b.updatedAt) return a.updatedAt - b.updatedAt;
  if (a.originId === b.originId) return 0;
  return a.originId > b.originId ? 1 : -1;
}

export interface StateIdentity {
  version: number;
  updatedAt: number;
  originId: string;
}

/** True if `candidate` should replace `current`. Equal states are NOT newer. */
export function isNewer(candidate: StateIdentity, current: StateIdentity): boolean {
  return compareStates(candidate, current) > 0;
}
